// Where an approver lets a Claude Code session read an environment's database for two
// hours. The session prints a code; typing it here, rather than following a link that
// carries it, is what stops a stranger's request being approved by accident.
//
// See docs/Y2026-Q4/design-attended-db-diagnostics.md.
import { useState } from "react";

import {
  useApproveDiagnosticsRequest,
  useDiagnosticsGrants,
  useLookupDiagnosticsRequest,
  useRevokeDiagnosticsGrant,
  type DiagnosticsGrant,
  type DiagnosticsGrantState,
} from "../api";
import { formatRelative } from "../utils/formatRelative";
import { formatWhen } from "../utils/formatWhen";
import { useDocumentTitle } from "../utils/useDocumentTitle";
import styles from "./IntegrationsRoute.module.css";
import own from "./DiagnosticsAccessRoute.module.css";

const STATE_LABEL: Record<DiagnosticsGrantState, string> = {
  pending: "Waiting for approval",
  lapsed: "Lapsed",
  approved: "Approved, not yet collected",
  active: "Active",
  expired: "Expired",
  revoked: "Revoked",
};

const Approve = () => {
  const [code, setCode] = useState("");
  const lookup = useLookupDiagnosticsRequest();
  const approve = useApproveDiagnosticsRequest();
  const found = lookup.data?.request;
  const approved = approve.data?.grant;

  const reset = () => {
    lookup.reset();
    approve.reset();
    setCode("");
  };

  return (
    <section className={styles.section}>
      <div className={styles.sectionHead}>
        <h2 className={styles.h2}>Approve a session</h2>
      </div>
      <form
        className={own.form}
        onSubmit={(e) => {
          e.preventDefault();
          approve.reset();
          lookup.mutate(code);
        }}
      >
        <input
          className={own.codeInput}
          aria-label="Code"
          placeholder="XXXX-XXXX"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          autoComplete="off"
        />
        <button type="submit" className={own.quietButton} disabled={!code || lookup.isLoading}>
          {lookup.isLoading ? "Looking up…" : "Look up"}
        </button>
      </form>
      {lookup.error ? <p className={own.error}>{lookup.error.message}</p> : null}
      {found ? (
        <div className={own.request}>
          <p className={own.purpose}>{found.purpose}</p>
          <p className={own.meta}>
            Asked {formatRelative(found.requested_at)}
            {found.requester_ip ? ` from ${found.requester_ip}` : ""}
          </p>
          {approved ? (
            <p className={own.done}>
              Approved. The session can read the database until{" "}
              {approved.expires_at ? formatWhen(approved.expires_at) : "the grant ends"}.
            </p>
          ) : (
            <>
              <p className={own.warning}>
                Approve only a code shown in a Claude Code session you are watching.
              </p>
              <div className={own.actions}>
                <button
                  type="button"
                  className={own.button}
                  disabled={approve.isLoading}
                  onClick={() => approve.mutate(found.user_code)}
                >
                  {approve.isLoading ? "Approving…" : "Approve for 2 hours"}
                </button>
                <button type="button" className={own.quietButton} onClick={reset}>
                  Cancel
                </button>
              </div>
              {approve.error ? <p className={own.error}>{approve.error.message}</p> : null}
            </>
          )}
        </div>
      ) : null}
    </section>
  );
};

const RevokeButton = ({ grant }: { grant: DiagnosticsGrant }) => {
  const revoke = useRevokeDiagnosticsGrant();
  if (grant.state !== "active" && grant.state !== "approved" && grant.state !== "pending") {
    return null;
  }
  return (
    <>
      <button
        type="button"
        className={own.quietButton}
        disabled={revoke.isLoading}
        onClick={() => revoke.mutate(grant.grant_id)}
      >
        {revoke.isLoading ? "Revoking…" : grant.state === "pending" ? "Refuse" : "Revoke"}
      </button>
      {revoke.error ? <p className={own.error}>{revoke.error.message}</p> : null}
    </>
  );
};

const ends = (grant: DiagnosticsGrant): string => {
  if (grant.revoked_at) return `${formatWhen(grant.revoked_at)} by ${grant.revoked_by_email}`;
  if (grant.expires_at) return formatWhen(grant.expires_at);
  return "";
};

const Grants = () => {
  const grants = useDiagnosticsGrants();
  return (
    <section className={styles.section}>
      <div className={styles.sectionHead}>
        <h2 className={styles.h2}>Recent requests</h2>
      </div>
      {grants.error ? <p className={own.error}>{grants.error.message}</p> : null}
      {grants.data && grants.data.grants.length === 0 ? (
        <p className={styles.empty}>None yet.</p>
      ) : null}
      {grants.data && grants.data.grants.length > 0 ? (
        <div className={styles.tableWrap}>
          <table className={own.table}>
            <thead>
              <tr>
                <th>Purpose</th>
                <th>State</th>
                <th>Asked</th>
                <th>Approved by</th>
                <th>Ends</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {grants.data.grants.map((grant) => (
                <tr key={grant.grant_id}>
                  <td>{grant.purpose}</td>
                  <td>{STATE_LABEL[grant.state]}</td>
                  <td>{formatWhen(grant.requested_at)}</td>
                  <td>{grant.approved_by_email ?? ""}</td>
                  <td>{ends(grant)}</td>
                  <td>
                    <RevokeButton grant={grant} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
};

export const DiagnosticsAccessRoute = () => {
  useDocumentTitle("Database access");
  return (
    <div className={styles.page}>
      <Approve />
      <Grants />
    </div>
  );
};
